import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Plus,
  Edit,
  Lock,
  Unlock,
  Trash2,
  CheckCircle,
  XCircle,
  Shield,
  ArrowLeft,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { SuperAdminLayout } from "@/components/SuperAdminLayout";

const API = process.env.REACT_APP_BACKEND_URL || "";

const getAdminAuthHeader = () => {
  const token = localStorage.getItem("admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const ROLE_TEMPLATES = {
  "Senior Admin": {
    restaurants: { read: true, write: true, delete: true },
    orders: { read: true, write: true, delete: true },
    reports: { read: true, write: true, delete: false },
    tickets: { read: true, write: true, delete: true },
    employees: { read: true, write: true, delete: true },
    audit_logs: { read: true, write: false, delete: false },
    dashboard: { read: true, write: false, delete: false },
  },
  "Junior Admin": {
    restaurants: { read: true, write: true, delete: false },
    orders: { read: true, write: false, delete: false },
    reports: { read: true, write: false, delete: false },
    tickets: { read: true, write: true, delete: false },
    employees: { read: true, write: false, delete: false },
    audit_logs: { read: true, write: false, delete: false },
    dashboard: { read: true, write: false, delete: false },
  },
  Analyst: {
    restaurants: { read: true, write: false, delete: false },
    orders: { read: true, write: false, delete: false },
    reports: { read: true, write: true, delete: false },
    tickets: { read: false, write: false, delete: false },
    employees: { read: false, write: false, delete: false },
    audit_logs: { read: true, write: false, delete: false },
    dashboard: { read: true, write: false, delete: false },
  },
  "Support Agent": {
    restaurants: { read: true, write: false, delete: false },
    orders: { read: true, write: false, delete: false },
    reports: { read: false, write: false, delete: false },
    tickets: { read: true, write: true, delete: false },
    employees: { read: false, write: false, delete: false },
    audit_logs: { read: true, write: false, delete: false },
    dashboard: { read: false, write: false, delete: false },
  },
};

export default function SuperAdminEmployees() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [showCredentialsDialog, setShowCredentialsDialog] = useState(false);
  const [newEmployeeCredentials, setNewEmployeeCredentials] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    password: "",
    role: "Junior Admin",
    phone: "",
    permissions: ROLE_TEMPLATES["Junior Admin"],
  });

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/api/super-admin/employees/`, {
        headers: getAdminAuthHeader(),
      });
      setEmployees(response.data);
    } catch (error) {
      console.error("Error fetching employees:", error);
      toast.error("Failed to load employees");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingEmployee) {
        // Update
        await axios.put(
          `${API}/api/super-admin/employees/${editingEmployee.id}`,
          {
            name: formData.name,
            role: formData.role,
            phone: formData.phone,
            permissions: formData.permissions,
          },
          { headers: getAdminAuthHeader() }
        );
        toast.success("Employee updated successfully");
        setShowDialog(false);
        resetForm();
        fetchEmployees();
      } else {
        // Create - Store credentials to show after creation
        const credentials = {
          email: formData.email,
          password: formData.password,
          name: formData.name,
          role: formData.role,
        };

        await axios.post(`${API}/api/super-admin/employees/`, formData, {
          headers: getAdminAuthHeader(),
        });

        // Show credentials dialog
        setNewEmployeeCredentials(credentials);
        setShowCredentialsDialog(true);
        setShowDialog(false);
        resetForm();
        fetchEmployees();
      }
    } catch (error) {
      console.error("Error saving employee:", error);
      toast.error(error.response?.data?.detail || "Failed to save employee");
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          toast.success("Copied to clipboard");
        })
        .catch(() => {
          // Fallback for clipboard API failure
          copyFallback(text);
        });
    } else {
      copyFallback(text);
    }

    function copyFallback(text) {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        toast.success("Copied to clipboard");
      } catch (err) {
        toast.error("Failed to copy");
      }
      document.body.removeChild(textArea);
    }
  };

  const handleLockUnlock = async (employeeId, currentStatus) => {
    try {
      const endpoint = currentStatus === "locked" ? "unlock" : "lock";
      await axios.post(
        `${API}/api/super-admin/employees/${employeeId}/${endpoint}`,
        endpoint === "lock" ? { reason: "Manually locked by admin" } : {},
        { headers: getAdminAuthHeader() }
      );
      toast.success(`Employee ${endpoint}ed successfully`);
      fetchEmployees();
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to update employee status");
    }
  };

  const handleDelete = async (employeeId) => {
    if (!confirm("Are you sure you want to disable this employee?")) return;

    try {
      await axios.delete(`${API}/api/super-admin/employees/${employeeId}`, {
        headers: getAdminAuthHeader(),
      });
      toast.success("Employee disabled successfully");
      fetchEmployees();
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to disable employee");
    }
  };

  const openCreateDialog = () => {
    setEditingEmployee(null);
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      email: employee.email,
      name: employee.name,
      password: "",
      role: employee.role,
      phone: employee.phone || "",
      permissions: employee.permissions,
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      email: "",
      name: "",
      password: "",
      role: "Junior Admin",
      phone: "",
      permissions: ROLE_TEMPLATES["Junior Admin"],
    });
  };

  const handleRoleChange = (role) => {
    setFormData({
      ...formData,
      role,
      permissions: ROLE_TEMPLATES[role] || formData.permissions,
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: "bg-green-100 text-green-800",
      disabled: "bg-gray-100 text-gray-800",
      locked: "bg-red-100 text-red-800",
    };

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${
          styles[status] || styles.active
        }`}
      >
        {status}
      </span>
    );
  };

  return (
    <SuperAdminLayout>
      <div>
        {/* Page Header */}
        <div
          className="mb-8 flex justify-between w-full items-center px-6
  bg-gradient-to-r from-orange-500 to-red-500
  shadow-md text-white py-[1.65rem]"
        >
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate("/admin/dashboard")}
              variant="ghost"
              className="hover:bg-gray-100"
            >
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Super Admin Employees</h1>
              <p className=" mt-1">
                Manage internal admin team with role-based permissions
              </p>
            </div>
          </div>
          <Button
            onClick={openCreateDialog}
            className="bg-white text-red-600 hover:bg-orange-500 hover:text-white transition-colors duration-200"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Employee
          </Button>
        </div>
        <div className="p-8">
          {/* Content */}
          <div>
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
              </div>
            ) : (
              <div className="grid gap-4">
                {employees.map((employee) => (
                  <Card key={employee.id}>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <Shield className="w-5 h-5 text-red-600" />
                            <div>
                              <h3 className="text-lg font-semibold">
                                {employee.name}
                              </h3>
                              <p className="text-sm text-gray-600">
                                {employee.email}
                              </p>
                            </div>
                          </div>
                          <div className="mt-3 flex items-center gap-4 text-sm">
                            <span className="font-medium text-gray-700">
                              Role:{" "}
                              <span className="text-red-600">
                                {employee.role}
                              </span>
                            </span>
                            {employee.phone && (
                              <span className="text-gray-600">
                                📞 {employee.phone}
                              </span>
                            )}
                            {getStatusBadge(employee.status)}
                            {employee.last_login && (
                              <span className="text-gray-500">
                                Last login:{" "}
                                {new Date(
                                  employee.last_login
                                ).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditDialog(employee)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              handleLockUnlock(employee.id, employee.status)
                            }
                          >
                            {employee.status === "locked" ? (
                              <Unlock className="w-4 h-4" />
                            ) : (
                              <Lock className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => handleDelete(employee.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Create/Edit Dialog */}
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingEmployee ? "Edit Employee" : "Create New Employee"}
                </DialogTitle>
                <DialogDescription>
                  {editingEmployee
                    ? "Update employee details and permissions"
                    : "Add a new super admin employee to your team"}
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      required
                      disabled={!!editingEmployee}
                    />
                  </div>
                </div>

                {!editingEmployee && (
                  <div>
                    <Label>Password *</Label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) =>
                        setFormData({ ...formData, password: e.target.value })
                      }
                      required
                      minLength={6}
                      placeholder="Minimum 6 characters"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      This password will be used by the employee to login.
                      You'll see it after creation.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Role *</Label>
                    <select
                      value={formData.role}
                      onChange={(e) => handleRoleChange(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    >
                      {Object.keys(ROLE_TEMPLATES).map((role) => (
                        <option key={role} value={role}>
                          {role}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({ ...formData, phone: e.target.value })
                      }
                    />
                  </div>
                </div>

                <div className="pt-4">
                  <h4 className="font-semibold mb-3">Permissions</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {Object.entries(formData.permissions).map(
                      ([module, perms]) => (
                        <div key={module} className="border rounded-lg p-3">
                          <div className="font-medium capitalize mb-2">
                            {module.replace("_", " ")}
                          </div>
                          <div className="flex gap-3 text-xs">
                            <label className="flex items-center gap-1">
                              {perms.read ? (
                                <CheckCircle className="w-3 h-3 text-green-600" />
                              ) : (
                                <XCircle className="w-3 h-3 text-gray-400" />
                              )}
                              Read
                            </label>
                            <label className="flex items-center gap-1">
                              {perms.write ? (
                                <CheckCircle className="w-3 h-3 text-green-600" />
                              ) : (
                                <XCircle className="w-3 h-3 text-gray-400" />
                              )}
                              Write
                            </label>
                            <label className="flex items-center gap-1">
                              {perms.delete ? (
                                <CheckCircle className="w-3 h-3 text-green-600" />
                              ) : (
                                <XCircle className="w-3 h-3 text-gray-400" />
                              )}
                              Delete
                            </label>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowDialog(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit">
                    {editingEmployee ? "Update" : "Create"} Employee
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Credentials Dialog - Show after employee creation */}
          <Dialog
            open={showCredentialsDialog}
            onOpenChange={setShowCredentialsDialog}
          >
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  Employee Created Successfully
                </DialogTitle>
                <DialogDescription>
                  Share these login credentials with the new employee. They will
                  use these to login at the Super Admin portal.
                </DialogDescription>
              </DialogHeader>

              {newEmployeeCredentials && (
                <div className="space-y-4 py-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-600 uppercase">
                        Employee Name
                      </label>
                      <p className="text-sm font-medium text-gray-900 mt-1">
                        {newEmployeeCredentials.name}
                      </p>
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-gray-600 uppercase">
                        Role
                      </label>
                      <p className="text-sm font-medium text-gray-900 mt-1">
                        {newEmployeeCredentials.role}
                      </p>
                    </div>

                    <div className="border-t border-blue-200 pt-3 mt-3">
                      <label className="text-xs font-semibold text-red-600 uppercase">
                        Login Credentials
                      </label>
                      <div className="mt-2 space-y-2">
                        <div className="flex items-center justify-between bg-white rounded px-3 py-2">
                          <div>
                            <p className="text-xs text-gray-500">Email</p>
                            <p className="text-sm font-mono font-medium text-gray-900">
                              {newEmployeeCredentials.email}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              copyToClipboard(newEmployeeCredentials.email)
                            }
                          >
                            Copy
                          </Button>
                        </div>

                        <div className="flex items-center justify-between bg-white rounded px-3 py-2">
                          <div>
                            <p className="text-xs text-gray-500">Password</p>
                            <p className="text-sm font-mono font-medium text-gray-900">
                              {newEmployeeCredentials.password}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              copyToClipboard(newEmployeeCredentials.password)
                            }
                          >
                            Copy
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-xs text-yellow-800">
                      <strong>⚠️ Important:</strong> Make sure to save these
                      credentials securely. The password will not be shown
                      again. The employee should login at:
                    </p>
                    <p className="text-xs font-mono bg-white px-2 py-1 rounded mt-2 text-gray-900">
                      {window.location.origin}/admin/login
                    </p>
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button
                  onClick={() => {
                    setShowCredentialsDialog(false);
                    setNewEmployeeCredentials(null);
                  }}
                  className="w-full"
                >
                  I've Saved The Credentials
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </SuperAdminLayout>
  );
}
